import { useId, useState } from 'react';
import {
  fieldValue,
  isProtected,
  orderedPhotos,
  type AnyEvent,
  type ItemRecord,
} from '@intentionalist/shared';
import { deleteItem } from './mutations.js';

/**
 * What is left to do to an item, at the foot of the item's own page.
 *
 * Deleting used to be a button on every catalog row. It is a rare thing to do
 * and was costing a control's worth of width on every line of the screen the
 * catalog is actually read on -- and the photo-led grid never offered it at
 * all, so the list was paying for something its sibling view did without.
 *
 * Last on the page, under everything else, because that is where a destructive
 * action belongs: reached deliberately rather than passed on the way to
 * something else.
 *
 * The way out sits beside it, and that is why this is "other actions" rather
 * than the delete panel it started as. An item's page is six panels deep, so
 * someone who has read to the bottom of it is as far from the way back at the
 * top as the page can put them -- and the only control within reach was the
 * destructive one. A page whose nearest exit deletes something is asking for
 * the wrong press. The safe one goes first in the row for the same reason.
 */
export function ItemActions({
  item,
  onWrite,
  onDeleted,
  onBack,
}: {
  item: ItemRecord;
  onWrite: (events: AnyEvent[]) => void;
  /** Called once it is gone, so the caller can leave a page about nothing. */
  onDeleted: () => void;
  /*
   * Leaving by choice, which the caller happens to handle the same way as
   * leaving because the item is gone. Two props rather than one anyway: they
   * are the same destination for two different reasons, and a caller that
   * wanted to tell them apart -- to say what just happened, say -- should not
   * have to take this component apart first.
   */
  onBack: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const headingId = useId();

  const name = String(fieldValue(item, 'name') ?? '(unnamed)');
  const attached = describeAttachments(item);

  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId}>Other actions</h3>

      <div className="actions">
        <button type="button" onClick={onBack}>
          Back to catalog
        </button>

        <button
          type="button"
          // Not while it says "Cancel": that press undoes rather than destroys.
          className={confirming ? '' : 'danger'}
          onClick={() => setConfirming(!confirming)}
          aria-label={`Delete ${name}`}
        >
          {confirming ? 'Cancel' : 'Delete'}
        </button>
      </div>

      {confirming && (
        <div className="warn" role="alert">
          {/* NFR-UX-7 asks for a confirmation stating what it affects. The counts
              are the part worth pausing over: a record with four photos and a
              history behind it is a different thing to delete from an empty one. */}
          <p>
            Delete <strong>{name}</strong>?{' '}
            {attached === ''
              ? 'Nothing else is attached to it.'
              : `Its ${attached} ${attachmentVerb(item)} with it.`}
          </p>
          {/* NFR-SAFE-3 asks for individual confirmation on a protected item. The
              confirmation already exists for every item, so what protection adds
              here is saying so -- someone who flagged this months ago needs
              reminding that they did. */}
          {isProtected(item) && <p>This item is marked protected.</p>}
          <p className="muted hint">
            {/* FR-3.8. Reversible is not the same as harmless, which is why this
                still asks -- but it is worth saying that nothing is lost today. */}
            It can be restored for 30 days, under Deleted.
          </p>
          <div className="actions">
            <button
              type="button"
              className="danger primary"
              onClick={() => {
                onWrite(deleteItem(item.itemId));
                setConfirming(false);
                // The page is about an item that is no longer in the catalog.
                onDeleted();
              }}
            >
              Delete it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * What goes with the item, in words.
 *
 * Only the parts that exist are named: "its 1 photo" is useful, "its 0 photos
 * and 0 verdicts" is noise that trains people to click through the confirmation
 * without reading it.
 */
function describeAttachments(item: ItemRecord): string {
  const photos = orderedPhotos(item).length;
  const intentions = item.intentions.size;

  const parts: string[] = [];
  if (photos > 0) parts.push(`${photos} photo${photos === 1 ? '' : 's'}`);
  if (intentions > 0) parts.push(`${intentions} verdict${intentions === 1 ? '' : 's'}`);

  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0]!;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]!}`;
}

/**
 * "goes" for one of a thing, "go" for anything else.
 *
 * The sentence always had two halves to list until recorded uses were removed,
 * so the plural was always right and the agreement was never tested. With one
 * attachment left it reads "its 1 verdict go with it".
 */
function attachmentVerb(item: ItemRecord): string {
  const only = orderedPhotos(item).length + item.intentions.size === 1;
  return only ? 'goes' : 'go';
}
